import { differenceInCalendarDays, parseISO } from "date-fns";
import type { BulkPropertyRow, ReuseCandidate } from "../types";
import { type AcquirePolicy, usesNew } from "./acquirePolicy";
import { isAllMattersField } from "./checkboxFields";
import { FIELDS_BY_ZENBUJIKO_OPTION, findCandidate } from "./reuseCandidates";
import { eligibleZenbujikoConstructions } from "./zenbujikoConstruction";

/** チェック列 → 依頼の書類種別。全部事項4列（kyoutan 等）はここに含めず ZENBUJIKO として扱う */
export const PICTURE_TYPE_BY_FIELD: Record<string, string> = {
  ownerInfo: "SHOYUSHAJIKO",
  tisekiDrawings: "CHISEKISOKURYOZU",
  electricDrawings: "DENSHIKOZU",
  tiekiDrawings: "CHIEKIKENZUMEN",
  buildingDrawings: "TATEMONOZUMEN",
};

/** チェック列 → 依頼の書類種別。全部事項4列は目録種別を問わず ZENBUJIKO 扱い */
export const pictureTypeOfField = (field: string): string | null =>
  isAllMattersField(field) ? "ZENBUJIKO" : PICTURE_TYPE_BY_FIELD[field] ?? null;

/**
 * 候補が時間窓内か。BE の判定（取得日 >= 今日 − 経過日数、日単位）に揃え、境界日を含む。
 * 取得日 null は窓判定では候補なし扱い
 *
 * @example
 * // 今日が 2026-09-14 のとき
 * isWithinWindow({ latestReceivedDate: "2026-03-02", ... }, 196, now) // true（ちょうど196日前）
 * isWithinWindow({ latestReceivedDate: "2026-03-01", ... }, 196, now) // false
 */
export const isWithinWindow = (
  candidate: ReuseCandidate,
  pastDays: number,
  now: Date
): boolean =>
  candidate.latestReceivedDate != null &&
  differenceInCalendarDays(now, parseISO(candidate.latestReceivedDate)) <=
    pastDays;

/** 候補が時間窓の制約を受けるか（ignoreWindow なら常に可、それ以外は isWithinWindow に従う） */
export const isUsableByWindow = (
  candidate: ReuseCandidate,
  policy: AcquirePolicy,
  pastDays: number,
  now: Date
): boolean => policy.ignoreWindow || isWithinWindow(candidate, pastDays, now);

/**
 * 他ユーザーのリユース候補。reusablePictures は自身の取得済み分も含むため、
 * 取得済みと同じ明細を除く（取得済みを使わない条件で自分の登記をリユース扱いにしないため）
 */
export const othersReusablePictures = (
  row: BulkPropertyRow
): ReuseCandidate[] =>
  row.reusablePictures.filter(
    (candidate) =>
      !row.acquiredPictures.some(
        ({ detailId }) => detailId === candidate.detailId
      )
  );

/**
 * 全部事項候補のオプション構成に対応するチェック列（目録なし構成は該当列がなく空になる）。
 * 現在事項は複数の目録から導かれるため重複を取り除く
 */
export const zenbujikoFields = (candidate: ReuseCandidate): string[] => [
  ...new Set(
    candidate.zenbujikoOptions.flatMap(
      (option) => FIELDS_BY_ZENBUJIKO_OPTION[option] ?? []
    )
  ),
];

// 新規取得を使う条件は候補がなくても取得できるため制限しない（null を返す）。
// それ以外は steps の順に候補プールを並べる（selectCandidate と同じ探索順）
export const pickCandidatePool = (
  row: BulkPropertyRow,
  policy: AcquirePolicy
): ReuseCandidate[][] | null => {
  if (usesNew(policy)) {
    return null;
  }
  return policy.steps.map((step) =>
    step === "acquired" ? row.acquiredPictures : othersReusablePictures(row)
  );
};

/**
 * 取得条件×経過日数で満たせないセルか（リユース候補ベースの可否）。
 * 種別による図面列の可否（checkboxAvailability）とは独立に判定する。
 * 全部事項4列は窓内 ZENBUJIKO 候補があり、かつその列が候補のオプション構成に
 * 含まれる場合のみ可（候補と同じ構成のチェックだけがリユースを成立させられるため）
 */
export const isReuseCheckboxDisabled = (
  field: string,
  row: BulkPropertyRow,
  policy: AcquirePolicy,
  pastDays: number,
  now: Date,
  excludeAcquired = false
): boolean => {
  const pools = pickCandidatePool(row, policy);
  if (pools == null) {
    return false;
  }

  if (isAllMattersField(field)) {
    // 構成選択肢（eligibleZenbujikoConstructions）と同じ往復一致フィルタを使う。
    // ここだけ緩い判定だと、送信時に構成不一致で依頼から黙って落ちるチェックを可能扱いしてしまう
    return !eligibleZenbujikoConstructions(
      row,
      policy,
      pastDays,
      now,
      excludeAcquired
    ).some((fields) => fields.includes(field));
  }

  const pictureType = PICTURE_TYPE_BY_FIELD[field];
  if (pictureType == null) {
    return false;
  }
  const isEligible = (candidate: ReuseCandidate | undefined): boolean =>
    candidate != null && isUsableByWindow(candidate, policy, pastDays, now);
  return !pools.some((candidates) =>
    isEligible(findCandidate(candidates, pictureType))
  );
};
